/**
 * Component Registry - Single Source of Truth
 *
 * Loads component descriptors from the backend and maps them to React components.
 * The backend provides validation schemas and metadata via describeAllComponents API.
 */

// React component implementations (presentational components)
import StackComponent from '../components/containers/Stack';
import SectionComponent from '../components/containers/Section';
import TextComponent from '../components/blocks/Text';
import ColumnsComponent from '../components/containers/Columns';
import HeaderComponent from '../components/containers/Header';
import FooterComponent from '../components/containers/Footer';
import ContentBodyComponent from '../components/containers/ContentBody';
import FloatingComponent from '../components/containers/Floating';

import ProductsComponent from '../components/blocks/Products';
import SuggestionsComponent from '../components/blocks/Suggestions';
import CatSuggestionsComponent from '../components/blocks/CatSuggestions';
import CategoriesComponent from '../components/blocks/Categories';
import PillsComponent from '../components/blocks/Pills';
import FiltersButtonComponent from '../components/blocks/FiltersButton';
import FacetsComponent from '../components/blocks/Facets';
import SortComponent from '../components/blocks/Sort';
import PaginationComponent from '../components/blocks/Pagination';
import FallbackWarningComponent from '../components/blocks/FallbackWarning';
import NoResultsComponent from '../components/blocks/NoResults';
import DidYouMeanComponent from '../components/blocks/DidYouMean';
import HistoryComponent from '../components/blocks/History';
import PopularQueriesComponent from '../components/blocks/PopularQueries';
import ImageDropzoneComponent from '../components/blocks/ImageDropzone';
import ViewAllComponent from '../components/blocks/ViewAll';

/**
 * React component mapping
 * Maps component type to its React implementation
 */
const REACT_COMPONENTS = {
  // Containers
  stack: StackComponent,
  section: SectionComponent,
  text: TextComponent,
  columns: ColumnsComponent,
  header: HeaderComponent,
  footer: FooterComponent,
  contentBody: ContentBodyComponent,
  floating: FloatingComponent,

  // Blocks
  products: ProductsComponent,
  suggestions: SuggestionsComponent,
  catSuggestions: CatSuggestionsComponent,
  categories: CategoriesComponent,
  pills: PillsComponent,
  filtersButton: FiltersButtonComponent,
  facets: FacetsComponent,
  sort: SortComponent,
  pagination: PaginationComponent,
  fallbackWarning: FallbackWarningComponent,
  noResults: NoResultsComponent,
  didYouMean: DidYouMeanComponent,
  history: HistoryComponent,
  popularQueries: PopularQueriesComponent,
  imageDropzone: ImageDropzoneComponent,
  viewAll: ViewAllComponent,
};

/**
 * Component Registry
 * Manages component descriptors from backend and their React implementations
 */
class ComponentRegistry {
  constructor() {
    this.client = null;
    this.descriptors = {}; // Component descriptors from backend
    this.loaded = false;
  }

  /**
   * Initialize registry with API client
   */
  initialize(client) {
    if (!client || typeof client.getSearchComponents !== 'function') {
      throw new Error('Client must implement getSearchComponents(shopId)');
    }
    this.client = client;
  }

  /**
   * Load component descriptors from backend
   * @param {string} shopId
   * @returns {Promise<Object>} Component descriptors
   */
  async loadComponents(shopId) {
    if (!this.client) {
      throw new Error('Registry not initialized. Call initialize() first.');
    }
    const response = await this.client.getSearchComponents(shopId);
    if (!response.success || !response.components) {
      throw new Error('Failed to load components from server');
    }
    this.descriptors = response.components;
    this.loaded = true;
    return this.descriptors;
  }

  /**
   * Get component descriptor by type
   * @param {string} type - Component type (e.g., 'stack', 'products')
   * @returns {Object|null} Component descriptor with validation schema
   */
  getDescriptor(type) {
    if (!this.loaded) {
      // eslint-disable-next-line no-console
      console.warn('Components not loaded yet. Call loadComponents() first.');
      return null;
    }

    return this.descriptors[type] || null;
  }

  /**
   * Get React component implementation by type
   * @param {string} type - Component type
   * @returns {React.Component|null} React component
   */
  // eslint-disable-next-line class-methods-use-this
  getReactComponent(type) {
    return REACT_COMPONENTS[type] || null;
  }

  /**
   * Get full component info (descriptor + React component)
   * @param {string} type - Component type
   * @returns {Object|null} { descriptor, component }
   */
  getComponent(type) {
    const descriptor = this.getDescriptor(type);
    const component = this.getReactComponent(type);

    if (!descriptor) {
      return null;
    }

    return {
      descriptor,
      component,
    };
  }

  /**
   * Get all available component types
   * @returns {string[]} Array of component types
   */
  getAllTypes() {
    return Object.keys(this.descriptors);
  }

  /**
   * Get components by category
   * @param {string} category - Component category
   * @returns {Object[]} Array of component descriptors
   */
  getByCategory(category) {
    return Object.entries(this.descriptors)
      .filter(([, descriptor]) => descriptor.category === category)
      .map(([type, descriptor]) => ({
        type,
        ...descriptor,
        component: this.getReactComponent(type),
      }));
  }

  /**
   * Validate component props using backend schema
   * @param {string} type - Component type
   * @param {Object} props - Props to validate
   * @returns {Object} { valid: boolean, errors: string[] }
   */
  validateProps(type, props) {
    const descriptor = this.getDescriptor(type);

    if (!descriptor) {
      return {
        valid: false,
        errors: [`Unknown component type: ${type}`],
      };
    }

    const errors = [];

    // Validate using property schemas from backend
    if (descriptor.properties) {
      Object.entries(descriptor.properties).forEach(([propName, propDef]) => {
        const value = props[propName];

        // Use validator.describe() schema if available
        if (propDef.schema) {
          // Schema contains validation rules from backend
          // This is where we'd integrate with backend validators
          // For now, basic validation based on schema metadata

          if (propDef.required && value === undefined) {
            errors.push(`Property '${propName}' is required`);
          }

          // Type validation would go here based on schema
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Get default props for a component
   * @param {string} type - Component type
   * @returns {Object} Default props
   */
  getDefaultProps(type) {
    const descriptor = this.getDescriptor(type);

    if (!descriptor || !descriptor.properties) {
      return {};
    }

    const defaults = {};

    Object.entries(descriptor.properties).forEach(([propName, propDef]) => {
      // Support both 'def' and 'default' field names
      const defaultValue = propDef.default !== undefined ? propDef.default : propDef.def;
      if (defaultValue !== undefined) {
        defaults[propName] = typeof defaultValue === 'function' ? defaultValue() : defaultValue;
      }
    });

    return defaults;
  }

  /**
   * Check if component is loaded
   * @returns {boolean}
   */
  isLoaded() {
    return this.loaded;
  }

  /**
   * Check if component type exists
   * @param {string} type
   * @returns {boolean}
   */
  hasComponent(type) {
    return type in this.descriptors && type in REACT_COMPONENTS;
  }
}

// Singleton instance
const registry = new ComponentRegistry();

export default registry;

// Export convenience methods
export const initializeRegistry = (client) => registry.initialize(client);
export const loadComponents = (shopId) => registry.loadComponents(shopId);
export const getComponent = (type) => registry.getComponent(type);
export const getDescriptor = (type) => registry.getDescriptor(type);
export const getReactComponent = (type) => registry.getReactComponent(type);
export const getAllTypes = () => registry.getAllTypes();
export const getByCategory = (category) => registry.getByCategory(category);
export const validateProps = (type, props) => registry.validateProps(type, props);
export const getDefaultProps = (type) => registry.getDefaultProps(type);
export const hasComponent = (type) => registry.hasComponent(type);
export const isLoaded = () => registry.isLoaded();
